import React, { useState, useRef, useEffect } from "react";
import {
  Menu,
  X,
  Search,
  LogIn,
  User,
  Settings,
  BarChart3,
  LogOut,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import BrandLogo from "./BrandLogo";
import MainNav from "./navigation/MainNav";
import NavLinks from "./navigation/NavLinks";
import NotificationBell from "./navigation/NotificationBell";
import Button from "./Button";
import { cn } from "../utils/cn";

const navLinks = [
  { name: "News", path: "/news" },
  { name: "Discover", path: "/discover" },
  { name: "Events", path: "/events" },
  { name: "Tracks", path: "/tracks" },
  { name: "Artists", path: "/artists" },
  { name: "Jam Studio", path: "/collaborate" },
  { name: "Community", path: "/community" },
  // { name: "Messages", path: "/messages" },
];

const Navbar: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const desktopAccountMenuRef = useRef<HTMLDivElement>(null);
  const mobileAccountMenuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleAccountMenu = () => {
    setIsAccountMenuOpen(!isAccountMenuOpen);
  };

  // Close account menu when clicking outside. The desktop block and the
  // mobile block each mount their own account menu; both need their own ref
  // or they clobber each other (the hidden-via-CSS but still-mounted desktop
  // div otherwise wins, and every tap on the mobile avatar looks "outside"
  // and closes the menu on the same gesture that opened it).
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      const insideDesktop =
        desktopAccountMenuRef.current?.contains(target) ?? false;
      const insideMobile =
        mobileAccountMenuRef.current?.contains(target) ?? false;
      if (!insideDesktop && !insideMobile) {
        setIsAccountMenuOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, {
      passive: true,
    });
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // Handle menu item navigation safely without race conditions
  const handleMenuNavigation = (
    path: string,
    e: React.MouseEvent | React.TouchEvent,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAccountMenuOpen(false);
    setIsMenuOpen(false);
    navigate(path);
  };

  const handleLogout = async (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await logout();
    setIsAccountMenuOpen(false);
    setIsMenuOpen(false);
    navigate("/");
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/discover?q=${encodeURIComponent(searchQuery)}`);
      setIsSearchOpen(false);
      setSearchQuery("");
      setIsMenuOpen(false);
    }
  };

  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  return (
    <MainNav>
      {/* Logo Section */}
      <BrandLogo />

      {/* Desktop Navigation */}
      <div className="hidden md:flex items-center h-full">
        <NavLinks links={navLinks} />
      </div>

      {/* Search and User Actions */}
      <div className="hidden md:flex items-center gap-4">
        {isSearchOpen ? (
          <form
            onSubmit={handleSearchSubmit}
            className="flex items-center bg-white/10 rounded-full px-3 py-1.5 border border-white/20"
          >
            <Search size={16} className="text-white/60 mr-2" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              className="bg-transparent border-none outline-none text-white text-sm w-40 placeholder:text-white/40"
              onBlur={() => {
                if (!searchQuery) setIsSearchOpen(false);
              }}
            />
            <button
              type="button"
              onClick={() => {
                setIsSearchOpen(false);
                setSearchQuery("");
              }}
              className="text-white/60 hover:text-white"
            >
              <X size={16} />
            </button>
          </form>
        ) : (
          <button
            onClick={() => setIsSearchOpen(true)}
            className="p-2 text-white/80 hover:text-white transition-colors duration-200"
            aria-label="Search"
          >
            <Search size={20} />
          </button>
        )}

        {isAuthenticated ? (
          <>
            <NotificationBell count={2} />
            <div className="relative" ref={desktopAccountMenuRef}>
              <button
                onClick={toggleAccountMenu}
                className="w-8 h-8 rounded-full bg-[#575AD0] text-white hover:bg-[#575AD0]/90 transition-colors duration-200 flex items-center justify-center"
                aria-label="User menu"
                aria-expanded={isAccountMenuOpen}
              >
                <span className="text-sm font-medium">
                  {user?.username?.charAt(0).toUpperCase() || "U"}
                </span>
              </button>

              {/* Account Dropdown Menu */}
              {isAccountMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 rounded-lg bg-background-dark border border-white/10 shadow-lg py-1 z-50">
                  <div className="px-4 py-2 border-b border-white/10">
                    <p className="text-white font-medium">
                      {user?.username || "User"}
                    </p>
                    <p className="text-white-medium text-xs truncate">
                      {user?.email}
                    </p>
                  </div>
                  <ul>
                    <li>
                      <Link
                        to="/profile"
                        onClick={() => setIsAccountMenuOpen(false)}
                        className="flex items-center w-full px-4 py-2 text-white-medium hover:text-white hover:bg-white/5 text-left"
                      >
                        <User size={16} className="mr-2" />
                        Profile
                      </Link>
                    </li>
                    <li className="border-t border-white/10">
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full text-left px-4 py-2 text-white-medium hover:text-white hover:bg-white/5"
                      >
                        <LogOut size={16} className="mr-2" />
                        Sign Out
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center gap-3">
            <div className="relative" ref={desktopAccountMenuRef}>
              <button
                onClick={toggleAccountMenu}
                className="p-2 text-white/80 hover:text-white transition-colors duration-200"
                aria-label="Account menu"
                aria-expanded={isAccountMenuOpen}
              >
                <User size={20} />
              </button>

              {/* Login Dropdown Menu */}
              {isAccountMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 rounded-lg bg-background-dark border border-white/10 shadow-lg py-2 z-50">
                  <Link
                    to="/login"
                    onClick={() => setIsAccountMenuOpen(false)}
                    className="flex items-center w-full text-left px-4 py-2 text-white-medium hover:text-white hover:bg-white/5"
                  >
                    <LogIn size={16} className="mr-2" />
                    Sign In
                  </Link>
                  <div className="px-4 py-2 text-white-medium text-xs mt-1">
                    Don't have an account?
                  </div>
                  <Link
                    to="/register"
                    onClick={() => setIsAccountMenuOpen(false)}
                    className="mx-4 mb-2 flex items-center justify-center px-3 py-1.5 text-sm w-[calc(100%-2rem)] bg-gold hover:bg-gold-600 text-black font-medium rounded"
                  >
                    Sign Up
                  </Link>
                  <Link
                    to="/register/artist"
                    onClick={() => setIsAccountMenuOpen(false)}
                    className="mx-4 mb-1 flex items-center justify-center px-3 py-1.5 text-sm w-[calc(100%-2rem)] border border-gold/50 text-gold hover:bg-gold/10 font-medium rounded transition-colors"
                  >
                    Apply as Artist
                  </Link>
                </div>
              )}
            </div>
            <Link to="/login">
              <Button
                variant="text"
                size="small"
                className="text-white-medium hover:text-white"
              >
                <LogIn size={16} className="mr-1.5" />
                Log In
              </Button>
            </Link>
            <Link to="/register">
              <Button variant="secondary" size="small">
                Sign Up
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Mobile menu button */}
      <div className="flex md:hidden items-center gap-4">
        <button
          className="p-3 -m-1 text-white/80 hover:text-white transition-colors duration-200 min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label="Search"
          onClick={() => {
            setIsSearchOpen(!isSearchOpen);
            // Close mobile menu if opening search
            if (!isSearchOpen) setIsMenuOpen(false);
          }}
        >
          {isSearchOpen ? <X size={22} /> : <Search size={22} />}
        </button>

        {isAuthenticated && (
          <>
            <NotificationBell count={2} />
            {/* Mobile account button */}
            <div className="relative" ref={mobileAccountMenuRef}>
              <button
                onClick={toggleAccountMenu}
                className="w-8 h-8 rounded-full bg-[#575AD0] text-white hover:bg-[#575AD0]/90 transition-colors duration-200 flex items-center justify-center"
                aria-label="User menu"
              >
                <span className="text-sm font-medium">
                  {user?.username?.charAt(0).toUpperCase() || "U"}
                </span>
              </button>

              {/* Mobile Account Dropdown Menu */}
              {isAccountMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 rounded-lg bg-background-dark border border-white/10 shadow-lg py-1 z-50">
                  <div className="px-4 py-2 border-b border-white/10">
                    <p className="text-white font-medium">
                      {user?.username || "User"}
                    </p>
                    <p className="text-white-medium text-xs truncate">
                      {user?.email}
                    </p>
                  </div>
                  <ul>
                    <li>
                      <Link
                        to="/profile"
                        className="flex items-center px-4 py-2 text-white-medium hover:text-white hover:bg-white/5"
                        onClick={() => setIsAccountMenuOpen(false)}
                      >
                        <User size={16} className="mr-2" />
                        Profile
                      </Link>
                    </li>
                    <li className="border-t border-white/10">
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full text-left px-4 py-2 text-white-medium hover:text-white hover:bg-white/5"
                      >
                        <LogOut size={16} className="mr-2" />
                        Sign Out
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </>
        )}

        <button
          onClick={toggleMenu}
          className={cn(
            "p-3 -m-1 text-white/80 hover:text-white transition-colors duration-200 min-h-[44px] min-w-[44px] flex items-center justify-center",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#575AD0] focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          )}
          aria-expanded={isMenuOpen}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
        >
          {isMenuOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Mobile menu */}
      {(isMenuOpen || isSearchOpen) && (
        <div
          className="md:hidden absolute top-full left-0 right-0 bg-[#01020E]/95 backdrop-blur-xl border-t border-white/5 shadow-2xl z-40"
          style={{
            animation: "slideDown 0.2s ease-out forwards",
          }}
        >
          <div className="p-4 space-y-3">
            {isSearchOpen ? (
              <form onSubmit={handleSearchSubmit} className="relative mb-6">
                <Search
                  className="absolute left-3 top-3 text-white/60"
                  size={18}
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search artists, tracks, or news..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg py-3 pl-10 pr-4 text-white focus:outline-none focus:border-gold transition-colors"
                />
              </form>
            ) : (
              <>
                <NavLinks
                  links={navLinks}
                  isMobile={true}
                  onLinkClick={() => setIsMenuOpen(false)}
                />

                {!isAuthenticated ? (
                  <div className="pt-3 border-t border-white/10 mt-4 flex flex-col space-y-3">
                    <Button
                      variant="primary"
                      size="small"
                      onClick={() => navigate("/register")}
                      className="bg-gold hover:bg-gold-600 text-black font-bold h-9 px-5 rounded-full shadow-glow-gold-subtle"
                    >
                      Sign Up
                    </Button>
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => navigate("/login")}
                      className="border-white/10 hover:bg-white/5 h-9 px-5 rounded-full text-white/90"
                    >
                      Log In
                    </Button>
                    <button
                      onClick={(e) =>
                        handleMenuNavigation("/register/artist", e)
                      }
                      className="w-full mt-2"
                    >
                      <Button
                        variant="outline"
                        fullWidth
                        className="border-gold/30 text-gold hover:bg-gold/10"
                      >
                        Apply as Artist
                      </Button>
                    </button>
                  </div>
                ) : (
                  <div className="pt-3 border-t border-white/10 mt-4 space-y-3">
                    <Link
                      to="/profile"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex w-full items-center px-4 py-3 text-white-medium hover:text-white hover:bg-white/5 text-left"
                    >
                      <User size={18} className="mr-3" />
                      Profile
                    </Link>
                    <Link
                      to="/profile"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex w-full items-center px-4 py-3 text-white-medium hover:text-white hover:bg-white/5 text-left"
                    >
                      <Settings size={18} className="mr-3" />
                      Edit Profile
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center px-4 py-3 text-red-400 hover:text-red-300 hover:bg-white/5 text-left"
                    >
                      <LogOut size={18} className="mr-3" />
                      Sign Out
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Add keyframe animation for mobile menu */}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </MainNav>
  );
};

export default Navbar;
